import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, MapPin, Users, ArrowLeft, Cpu, X, Eye, Bell, Network, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Kanan = ({ t, isDark }) => {
    const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        if (isPrivacyOpen) {
            htmlElement.style.overflow = 'hidden';
            htmlElement.style.height = '100%';
            bodyElement.style.overflow = 'hidden';
            bodyElement.style.height = '100%';
            bodyElement.setAttribute('data-modal-open', 'true');
        } else {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        }

        return () => {
            htmlElement.style.overflow = '';
            htmlElement.style.height = '';
            bodyElement.style.overflow = '';
            bodyElement.style.height = '';
            bodyElement.removeAttribute('data-modal-open');
        };
    }, [isPrivacyOpen]);

    const k = t?.kanan || {};

    const appStoreUrl = "https://apps.apple.com/mx/app/kanan-app/id6763949034";
    const playStoreUrl = "https://play.google.com/store/apps/details?id=com.tdsmx.kanan&hl=es_MX";

    return (
        <div className={`min-h-screen pt-32 pb-24 px-6 transition-colors duration-700
      ${isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900'}`}>

            <style dangerouslySetInnerHTML={{__html: `
                .no-scrollbar::-webkit-scrollbar {
                    display: none !important;
                    width: 0 !important;
                    height: 0 !important;
                }
                .no-scrollbar {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                }
                
                body[data-modal-open="true"] .nav-container {
                    z-index: 0 !important;
                    pointer-events: none !important;
                    opacity: 0 !important;
                    transition: opacity 0.3s ease;
                }
            `}} />

            <div className="container mx-auto max-w-5xl relative z-10">

                <Link to="/" className={`inline-flex items-center gap-2 mb-12 font-black uppercase text-[10px] tracking-widest transition-all hover:-translate-x-1
          ${isDark ? 'text-slate-400' : 'text-slate-600 hover:text-blue-600'}`}>
                    <ArrowLeft size={16} /> {k.back_home || "Volver al Inicio"}
                </Link>

                {/* HERO SECTION */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center mb-16">
                    <div className="md:col-span-7 text-left">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />
                            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-500">{k.badge || "DISPONIBLE AHORA"}</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tighter italic mb-6">
                            {k.hero_title || "Kanan"}
                        </h1>
                        <p className="text-xl font-bold leading-relaxed opacity-80 mb-8">
                            {k.hero_subtitle}
                        </p>

                        {/* STORE BUTTONS & PRIVACY */}
                        <div className="flex flex-wrap items-center gap-3">
                            <a
                                href={appStoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`px-6 py-3.5 rounded-2xl border font-black uppercase text-[10px] tracking-widest transition-all flex items-center gap-2.5 active:scale-95 shadow-lg ${
                                    isDark 
                                        ? 'bg-white text-slate-950 border-white hover:bg-slate-200' 
                                        : 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
                                }`}
                            >
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.93-2.85-.9.04-1.99.6-2.61 1.35-.55.63-1.03 1.68-.9 2.7.99.08 2.01-.5 2.58-1.2"/>
                                </svg>
                                <span>App Store</span>
                                <ArrowUpRight size={14} className="opacity-60" />
                            </a>

                            <a
                                href={playStoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-6 py-3.5 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-[10px] tracking-widest transition-all flex items-center gap-2.5 shadow-lg shadow-blue-600/20 active:scale-95"
                            >
                                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path d="M3.609 1.814L13.793 12 3.61 22.186a2.023 2.023 0 0 1-.22-.962V2.776c0-.361.082-.693.219-.962zm11.242 11.243l2.257 2.257-11.69 6.68 9.433-8.937zm0-2.114L5.418 1.998l11.69 6.688-2.257 2.257zm1.488 1.487l2.805 1.603c.895.511.895 1.352 0 1.863l-2.805 1.603-2.128-2.535 2.128-2.534z"/>
                                </svg>
                                <span>Google Play</span>
                                <ArrowUpRight size={14} className="opacity-60" />
                            </a>

                            <button
                                onClick={() => setIsPrivacyOpen(true)}
                                className="px-5 py-3.5 bg-red-500/10 border border-red-500/20 hover:bg-red-500 hover:text-white text-red-500 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center gap-2.5 transition-all duration-300 shadow-lg shadow-red-500/5 cursor-pointer"
                            >
                                <Shield size={15} /> {k.privacy_btn || "Aviso de Privacidad"}
                            </button>
                        </div>
                    </div>

                    <div className="md:col-span-5 flex justify-center">
                        <div className={`w-64 h-64 rounded-[3rem] border backdrop-blur-3xl flex items-center justify-center relative shadow-2xl
              ${isDark ? 'bg-slate-900/60 border-white/10 shadow-black/50' : 'bg-white border-slate-200 shadow-blue-500/10'}`}>
                            
                            <div className="w-32 h-32 rounded-full overflow-hidden flex items-center justify-center bg-blue-500/5 border border-blue-500/20 p-2">
                                <img 
                                    src="/logo_kanan.png" 
                                    alt="Kanan App Logo" 
                                    className="w-full h-full object-contain"
                                />
                            </div>
                            
                            <div className="absolute -inset-4 rounded-[3.5rem] border border-blue-500/20 animate-spin" style={{ animationDuration: '15s' }} />
                        </div>
                    </div>
                </div>

                {/* PILARES DE IDENTIDAD DE MARCA */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 text-left">
                    <div className={`p-8 md:p-10 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-blue-500/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-blue-500 block mb-2">{k.brand_pillars?.meaning?.tag}</span>
                        <h2 className="text-2xl font-black uppercase tracking-tight mb-4">{k.brand_pillars?.meaning?.title}</h2>
                        <p className="text-sm font-bold opacity-60 leading-relaxed">
                            {k.brand_pillars?.meaning?.desc_p1}
                            <strong>{k.brand_pillars?.meaning?.desc_highlight1}</strong>
                            {k.brand_pillars?.meaning?.desc_p2}
                            <em>{k.brand_pillars?.meaning?.desc_highlight2}</em>
                            {k.brand_pillars?.meaning?.desc_p3}
                        </p>
                    </div>

                    <div className={`p-8 md:p-10 rounded-[3.5rem] border backdrop-blur-3xl
                        ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-blue-500/5'}`}>
                        <span className="text-[9px] font-black uppercase tracking-widest text-indigo-500 block mb-2">{k.brand_pillars?.logo?.tag}</span>
                        <h2 className="text-2xl font-black uppercase tracking-tight mb-4">{k.brand_pillars?.logo?.title}</h2>
                        <p className="text-sm font-bold opacity-60 leading-relaxed">
                            {k.brand_pillars?.logo?.desc_p1}
                            <strong>{k.brand_pillars?.logo?.highlight1}</strong>
                            {k.brand_pillars?.logo?.desc_p2}
                            <strong>{k.brand_pillars?.logo?.highlight2}</strong>
                            {k.brand_pillars?.logo?.desc_p3}
                            <strong>{k.brand_pillars?.logo?.highlight3}</strong>
                            {k.brand_pillars?.logo?.desc_p4}
                        </p>
                    </div>
                </div>

                {/* DESAFÍO Y SOLUCIÓN GENERAL */}
                <div className={`p-8 md:p-12 rounded-[3.5rem] border backdrop-blur-3xl mb-12 text-left
          ${isDark ? 'bg-slate-900/40 border-white/10' : 'bg-white border-slate-200 shadow-lg shadow-blue-500/5'}`}>
                    <span className="text-[9px] font-black uppercase tracking-widest text-blue-500 block mb-2">{k.vision?.tag}</span>
                    <h2 className="text-2xl font-black uppercase tracking-tight mb-6">{k.vision?.title}</h2>
                    <p className="text-base font-bold opacity-70 leading-relaxed">
                        {k.description}
                    </p>
                </div>

                {/* COMPONENTES DEL ECOSISTEMA HUMANO */}
                <h3 className="text-xs font-black uppercase tracking-[0.4em] text-blue-500 text-left mb-6 pl-4">{k.ecosystem?.section_title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 text-left mb-12">
                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-red-500/10 rounded-xl text-red-500 w-fit mb-4">
                            <Eye size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{k.ecosystem?.reports?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{k.ecosystem?.reports?.desc}</p>
                    </div>

                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-blue-500/10 rounded-xl text-blue-500 w-fit mb-4">
                            <Bell size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{k.ecosystem?.monitoring?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{k.ecosystem?.monitoring?.desc}</p>
                    </div>

                    <div className={`md:col-span-4 p-8 rounded-[2.5rem] border ${isDark ? 'bg-slate-900/20 border-white/5' : 'bg-white border-slate-100 shadow-sm'}`}>
                        <div className="p-3 bg-indigo-500/10 rounded-xl text-indigo-500 w-fit mb-4">
                            <Network size={20} />
                        </div>
                        <h4 className="text-lg font-black mb-2">{k.ecosystem?.community?.title}</h4>
                        <p className="text-xs font-bold opacity-50 leading-relaxed">{k.ecosystem?.community?.desc}</p>
                    </div>
                </div>

                {/* INFRAESTRUCTURA TÉCNICA */}
                <h4 className="text-xs font-black uppercase tracking-[0.4em] text-indigo-500 text-left mb-6 pl-4">{k.engineering?.section_title}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
                    {[
                        { icon: <MapPin size={24} />, title: k.engineering?.item1?.title, desc: k.engineering?.item1?.desc },
                        { icon: <Users size={24} />, title: k.engineering?.item2?.title, desc: k.engineering?.item2?.desc },
                        { icon: <Cpu size={24} />, title: k.engineering?.item3?.title, desc: k.engineering?.item3?.desc }
                    ].map((item, idx) => (
                        <div key={idx} className={`p-8 rounded-[2.5rem] border transition-all duration-300
              ${isDark ? 'bg-slate-900/30 border-white/5 hover:border-blue-500/20' : 'bg-white border-slate-200 hover:border-blue-500/20 shadow-md shadow-blue-500/5'}`}>
                            <div className="text-blue-500 mb-4">{item.icon}</div>
                            <h3 className="text-lg font-black mb-2">{item.title}</h3>
                            <p className="text-xs font-bold opacity-50 leading-relaxed">{item.desc}</p>
                        </div>
                    ))}
                </div>

            </div>

            {/* PRIVACY POLICY MODAL */}
            <AnimatePresence>
                {isPrivacyOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999999] flex items-center justify-center p-4 md:p-6 bg-slate-950/95 backdrop-blur-3xl"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            className={`relative w-full max-w-2xl p-6 sm:p-8 md:p-12 rounded-[2.5rem] md:rounded-[3rem] border shadow-2xl text-left flex flex-col max-h-[80vh]
                ${isDark ? 'bg-[#0E1526] border-white/10 text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                        >
                            <button
                                onClick={() => setIsPrivacyOpen(false)}
                                className={`absolute top-4 right-4 md:top-6 md:right-6 p-2 rounded-full border transition-colors z-[100]
                  ${isDark ? 'border-white/10 hover:bg-white/10 bg-[#0E1526]' : 'border-slate-200 hover:bg-slate-100 bg-white'}`}
                            >
                                <X size={18} />
                            </button>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 pr-10">
                                <div className="w-12 h-12 rounded-2xl overflow-hidden border border-red-500/20 flex items-center justify-center bg-red-500/10 shrink-0 p-2">
                                    <img 
                                        src="/logo192.png" 
                                        alt="Logo Kanan" 
                                        className="w-full h-full object-contain" 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-xl md:text-2xl font-black tracking-tight text-red-500 leading-tight">
                                        {k.privacy?.title}
                                    </h2>
                                    <p className="text-[8px] md:text-[9px] font-black uppercase tracking-wider opacity-40">
                                        {k.privacy?.badge}
                                    </p>
                                </div>
                            </div>

                            <div className="h-px bg-current opacity-10 mb-6 shrink-0" />
                            <div className="space-y-6 text-xs md:text-sm font-bold opacity-70 leading-relaxed overflow-y-auto pr-2 flex-grow no-scrollbar">
                                <h3 className="text-sm md:text-base font-black opacity-100 text-slate-500 dark:text-slate-400">
                                    {k.privacy?.subtitle}
                                </h3>
                                <p>{k.privacy?.p1}</p>
                                <p>{k.privacy?.p2}</p>
                                <p>{k.privacy?.p3}</p>
                                <p>{k.privacy?.p4}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default Kanan;